import { Button } from "../../ui/button";
import { cn } from "../../ui/cn";
import {
  type ImportStepKey,
  importSteps,
  isStepReachable,
  type StepContext,
  stepNumber,
} from "../stepper";

type StepperProps = Readonly<{
  current: ImportStepKey;
  context: StepContext;
  onSelect: (step: ImportStepKey) => void;
}>;

/**
 * The five steps as an ordered list. The current step carries `aria-current="step"`. A step that is
 * reachable is a button; one that is not is plain text with the word "not available yet", so the
 * state never depends on colour alone.
 */
export function Stepper({ current, context, onSelect }: StepperProps) {
  return (
    <nav aria-label="Import steps">
      <ol className="m-0 flex list-none flex-col flex-wrap gap-2 p-0 sm:flex-row lg:grid lg:grid-cols-5 lg:gap-3">
        {importSteps.map((step) => {
          const isCurrent = step.key === current;
          const reachable = isStepReachable(step.key, context);
          const number = stepNumber(step.key);
          return (
            <li
              key={step.key}
              aria-current={isCurrent ? "step" : undefined}
              className={cn(
                "flex min-h-11 items-center rounded-md border border-border bg-card compact:min-h-8",
                // The current step has a heavier border as well as a fill; an unavailable step has a dashed border: never colour alone.
                isCurrent && "border-2 border-primary bg-accent font-semibold text-link",
                !reachable && "border-dashed text-muted-foreground",
              )}
            >
              {reachable && !isCurrent ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(stepInner, "px-4")}
                  onClick={() => onSelect(step.key)}
                >
                  <StepNumber number={number} />
                  <span>{step.label}</span>
                </Button>
              ) : (
                <span className={stepInner}>
                  <StepNumber number={number} current={isCurrent} />
                  <span>{step.label}</span>
                  {isCurrent ? <span className="sr-only"> (current step)</span> : null}
                  {!reachable ? <span className="sr-only"> (not available yet)</span> : null}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

const stepInner =
  "inline-flex h-auto min-h-11 w-full cursor-pointer items-center justify-start gap-2 rounded-none bg-transparent px-4 text-left text-[15px] font-[inherit] text-inherit whitespace-normal compact:h-auto compact:min-h-8";

function StepNumber({ number, current = false }: Readonly<{ number: number; current?: boolean }>) {
  return (
    <span
      className={cn(
        "inline-flex size-6 shrink-0 items-center justify-center rounded-full border border-current text-[13px]",
        current && "border-primary bg-primary text-primary-foreground",
      )}
      aria-hidden="true"
    >
      {number}
    </span>
  );
}
